"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { useVirtualizer } from '@tanstack/react-virtual';
import deepEqual from 'fast-deep-equal';
import { useRef, useCallback, useEffect } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { DropdownUI } from '../../DropdownUI/index.mjs';
import { useNavigationContext } from './KeyboardNavigation/use-navigation.mjs';
import DropdownOption from './Option.mjs';

const NO_RESULTS_MESSAGE_COUNT = 1;
const SEPARATOR_COUNT = 1;
const placeholderItemStyles = "absolute top-0 left-0 w-full";
const virtualItemStyles = "absolute top-0 left-0 w-max min-w-full";
function VirtualizedDropdownList({
  options,
  fallbackOptions,
  isOptionSelected,
  handleOptionSelect,
  searchTerm
}) {
  const { currentItem } = useNavigationContext();
  const parentRef = useRef(null);
  const maxItemWidthRef = useRef(0);
  const innerContainerRef = useRef(null);
  const hasMainOptions = hasOptions(options);
  const hasFallbackOptions = hasOptions(fallbackOptions);
  const showSeparator = shouldShowSeparator(options, fallbackOptions);
  const showNoResultsMessage = shouldShowNoResultsMessage(options, fallbackOptions);
  const totalCount = getTotalCount(options, fallbackOptions, showSeparator, showNoResultsMessage);
  const updateContainerWidth = useCallback((width) => {
    requestAnimationFrame(() => {
      if (innerContainerRef.current) {
        innerContainerRef.current.style.width = `${width}px`;
      }
    });
  }, []);
  const debouncedUpdateContainerWidth = useDebounceCallback(updateContainerWidth, 100);
  const storeWidestItemSize = useCallback(
    (rect) => {
      if (rect.width > maxItemWidthRef.current) {
        maxItemWidthRef.current = rect.width;
        debouncedUpdateContainerWidth(rect.width);
      }
    },
    [maxItemWidthRef, debouncedUpdateContainerWidth]
  );
  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
    overscan: 15,
    measureElement: (element) => {
      const rect = element.getBoundingClientRect();
      storeWidestItemSize(rect);
      return rect.height;
    }
  });
  useEffect(() => {
    maxItemWidthRef.current = 0;
    if (innerContainerRef.current) {
      innerContainerRef.current.style.width = "auto";
    }
  }, [options, fallbackOptions]);
  useEffect(() => {
    if (!currentItem || currentItem === "drill-up") return;
    const currentIndex = findCurrentItemIndex(
      currentItem,
      options,
      fallbackOptions,
      showSeparator,
      showNoResultsMessage
    );
    if (currentIndex !== -1) {
      rowVirtualizer.scrollToIndex(currentIndex, { align: "auto" });
    }
  }, [currentItem, options, fallbackOptions, showSeparator, showNoResultsMessage, rowVirtualizer]);
  const getItemAtIndex = (index) => {
    if (showNoResultsMessage) {
      if (index === 0) {
        return { type: "no-results", searchTerm };
      }
      if (index === 1) {
        return { type: "separator" };
      }
      const fallbackOptionIndex2 = index - NO_RESULTS_MESSAGE_COUNT - SEPARATOR_COUNT;
      return {
        type: "option",
        option: fallbackOptions[fallbackOptionIndex2],
        isFromFallback: true
      };
    }
    if (index < options.length) {
      return {
        type: "option",
        option: options[index],
        isFromFallback: false
      };
    }
    const separatorIndex = options.length;
    if (showSeparator && index === separatorIndex) {
      return { type: "separator" };
    }
    const offsetForSeparator = showSeparator ? SEPARATOR_COUNT : 0;
    const fallbackStartIndex = options.length + offsetForSeparator;
    const fallbackOptionIndex = index - fallbackStartIndex;
    return {
      type: "option",
      option: fallbackOptions[fallbackOptionIndex],
      isFromFallback: true
    };
  };
  if (!hasMainOptions && !hasFallbackOptions) {
    return /* @__PURE__ */ jsx(DropdownUI.NoOptionsHeading, { text: searchTerm ? `No results found for: ${searchTerm}` : "No results found" });
  }
  return /* @__PURE__ */ jsx("div", { ref: parentRef, "data-virtualized-list": "true", className: "max-h-[80vh] overflow-auto relative w-max min-w-full", children: /* @__PURE__ */ jsx(
    "div",
    {
      ref: innerContainerRef,
      className: "relative min-w-full",
      style: {
        height: `${rowVirtualizer.getTotalSize()}px`
      },
      children: rowVirtualizer.getVirtualItems().map((virtualItem) => {
        const item = getItemAtIndex(virtualItem.index);
        if (item.type === "no-results") {
          return /* @__PURE__ */ jsx(
            "div",
            {
              ref: rowVirtualizer.measureElement,
              "data-index": virtualItem.index,
              className: placeholderItemStyles,
              style: {
                transform: `translateY(${virtualItem.start}px)`
              },
              children: /* @__PURE__ */ jsx(
                DropdownUI.NoOptionsHeading,
                {
                  text: item.searchTerm ? `No results found for: ${item.searchTerm}` : "No results found"
                }
              )
            },
            virtualItem.key
          );
        }
        if (item.type === "separator") {
          return /* @__PURE__ */ jsx(
            "div",
            {
              ref: rowVirtualizer.measureElement,
              "data-index": virtualItem.index,
              className: placeholderItemStyles,
              style: {
                transform: `translateY(${virtualItem.start}px)`
              },
              children: /* @__PURE__ */ jsx("hr", { className: "my-2" })
            },
            virtualItem.key
          );
        }
        return /* @__PURE__ */ jsx(
          "div",
          {
            ref: rowVirtualizer.measureElement,
            "data-index": virtualItem.index,
            className: virtualItemStyles,
            style: {
              transform: `translateY(${virtualItem.start}px)`
            },
            children: /* @__PURE__ */ jsx(
              DropdownOption,
              {
                option: item.option,
                selected: isOptionSelected(item.option),
                current: deepEqual(item.option, currentItem),
                onSelect: () => handleOptionSelect(item.option)
              }
            )
          },
          virtualItem.key
        );
      })
    }
  ) });
}
function hasOptions(options) {
  return options.length > 0;
}
function shouldShowSeparator(options, fallbackOptions) {
  return options.length > 0 && fallbackOptions.length > 0;
}
function shouldShowNoResultsMessage(options, fallbackOptions) {
  return options.length === 0 && fallbackOptions.length > 0;
}
function getTotalCount(options, fallbackOptions, showSeparator, showNoResultsMessage) {
  if (showNoResultsMessage) {
    return NO_RESULTS_MESSAGE_COUNT + SEPARATOR_COUNT + fallbackOptions.length;
  }
  const mainOptionsCount = options.length;
  const separatorCount = showSeparator ? SEPARATOR_COUNT : 0;
  const fallbackOptionsCount = fallbackOptions.length;
  return mainOptionsCount + separatorCount + fallbackOptionsCount;
}
function findCurrentItemIndex(currentItem, options, fallbackOptions, showSeparator, showNoResultsMessage) {
  if (showNoResultsMessage) {
    const fallbackIndex2 = fallbackOptions.findIndex((opt) => deepEqual(opt, currentItem));
    if (fallbackIndex2 !== -1) {
      return NO_RESULTS_MESSAGE_COUNT + SEPARATOR_COUNT + fallbackIndex2;
    }
    return -1;
  }
  const mainIndex = options.findIndex((opt) => deepEqual(opt, currentItem));
  if (mainIndex !== -1) {
    return mainIndex;
  }
  const fallbackIndex = fallbackOptions.findIndex((opt) => deepEqual(opt, currentItem));
  if (fallbackIndex !== -1) {
    const separatorOffset = showSeparator ? SEPARATOR_COUNT : 0;
    return options.length + separatorOffset + fallbackIndex;
  }
  return -1;
}

export { VirtualizedDropdownList };
